import * as React from "react";
import { Link } from "react-router";
import { Button, NudgeArrow } from "@/components/ui/Button";
import { PanelGrid } from "@/components/ui/PanelGrid";
import { cn } from "@/lib/cn";
import { Reveal } from "./Reveal";

/**
 * The marketing site's rhythm, in three pieces.
 *
 * They exist so that seven pages share one vertical rhythm rather than seven
 * near-identical stacks of padding utilities. The failure this prevents is
 * specific and hard to spot in review: a section written with `py-20` next to
 * one written with `py-24` looks fine in isolation and looks sloppy the moment
 * a reader scrolls between them.
 */

type Tone = "surface" | "sunken" | "panel";

export function Section({
  id,
  tone = "surface",
  bleedTop = false,
  className,
  children,
}: {
  id?: string;
  tone?: Tone;
  /** Removes the top padding, for a band that flows out of the one above it. */
  bleedTop?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      className={cn(
        "section",
        tone !== "surface" && `section--${tone}`,
        tone === "panel" && "on-panel",
        className
      )}
    >
      <div className={cn("container section__inner", bleedTop && "section__inner--bleed-top")}>
        {children}
      </div>
    </section>
  );
}

/**
 * The opening band on every marketing page, home included.
 *
 * Panel-toned so it reads as one surface with the header sitting on it, which is
 * what stops the navigation looking like a bar bolted to the top of a document.
 *
 * `size="lg"` is the home page: taller band, bigger headline, and a fixed-width
 * aside column so the device mock-up cannot stretch. It used to be a separate
 * hand-written hero in `Home.tsx`, which meant the site's most-viewed band was
 * the one band not sharing the hero's rules - its gutters and its panel grid had
 * already drifted from every other page's.
 */

type HeroSize = "md" | "lg";

export function PageHero({
  eyebrow,
  title,
  lead,
  size = "md",
  aside,
  children,
}: {
  eyebrow: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  size?: HeroSize;
  /** Rendered beside the copy on desktop, below it on a phone. */
  aside?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <section className="on-panel hero">
      <PanelGrid />
      <div className={cn("container hero__inner", size === "lg" && "hero__inner--lg")}>
        <div
          className={cn(
            Boolean(aside) && "hero__grid",
            Boolean(aside) && size === "lg" && "hero__grid--lg"
          )}
        >
          <div className={cn("hero__copy", Boolean(aside) && "hero__copy--with-aside")}>
            <p className="eyebrow eyebrow--panel-accent">{eyebrow}</p>
            <h1 className={cn("hero__title", size === "lg" && "hero__title--lg")}>{title}</h1>
            {lead && <p className={cn("hero__lead", size === "lg" && "hero__lead--lg")}>{lead}</p>}
            {children}
          </div>
          {aside}
        </div>
      </div>
    </section>
  );
}

/**
 * A section's opening.
 *
 * The eyebrow is a label, not a second heading - it names what kind of thing is
 * coming ("What it does", "Honest limits") so a reader scanning the page can
 * decide whether to slow down. Sentence-length eyebrows defeat that, so keep
 * them to two or three words.
 *
 * `size="sm"` is the intro that shares a row with something else rather than
 * spanning the band - the left column of a two-column section. It steps the
 * heading down so it does not out-shout the full-width intro further up the same
 * page. Both sizes were being written by hand before this prop existed, which is
 * why /company's and /home's identical two-column openings had drifted apart.
 */

type IntroSize = "sm" | "lg";

export function SectionIntro({
  eyebrow,
  title,
  lead,
  tone = "surface",
  size = "lg",
  align = "left",
  children,
  className,
}: {
  eyebrow?: string;
  title: React.ReactNode;
  lead?: React.ReactNode;
  tone?: Tone;
  size?: IntroSize;
  align?: "left" | "center";
  /** Extra copy under the lead - additional paragraphs, a link, a button. */
  children?: React.ReactNode;
  className?: string;
}) {
  const onPanel = tone === "panel";

  return (
    <Reveal className={cn("intro", align === "center" && "intro--center", className)}>
      {eyebrow && (
        <p className={cn("eyebrow", onPanel ? "eyebrow--panel-accent" : "eyebrow--accent")}>
          {eyebrow}
        </p>
      )}
      <h2
        className={cn(
          "intro__title",
          size === "sm" && "intro__title--sm",
          onPanel && "intro__title--panel"
        )}
      >
        {title}
      </h2>
      {lead && <p className={cn("intro__lead", onPanel && "intro__lead--panel")}>{lead}</p>}
      {children}
    </Reveal>
  );
}

/**
 * The closing call to action, on every marketing page.
 *
 * Two actions, and the secondary one is deliberately not "book a demo". The
 * product provisions a company in under a minute without a sales conversation,
 * so putting a calendar in front of someone who could be signed in by now would
 * be adding a step to sell a product whose pitch is removing steps.
 */
export function CtaBand({
  title = "Ninety seconds, before the next job starts.",
  lead = "Set up your company, invite a crew and run your first assessment today. No card, no sales call.",
}: {
  title?: string;
  lead?: string;
}) {
  return (
    <Section tone="panel">
      <SectionIntro tone="panel" align="center" title={title} lead={lead}>
        <div className="cta-actions">
          <Link to="/signup">
            <Button variant="panel" size="lg" trailingIcon={<NudgeArrow />}>
              Start free
            </Button>
          </Link>
          <Link to="/pricing">
            <Button variant="panel-ghost" size="lg">
              See pricing
            </Button>
          </Link>
        </div>
      </SectionIntro>
    </Section>
  );
}
